/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { memo } from "react";

import styled from "styled-components";

import {
  Box,
  Card,
  Heading,
  List,
  ListItem,
  Stack,
  Text,
} from "@origin-digital/ods-core";

import {
  BillFrequencyEnum,
  IISolarBatteryOutlookResponse,
  IISystemSizeMinMaxResponse,
} from "@/contract/generated/electrify-v1";
import {
  formatLabel,
  formatNumberWithCommas,
  formatWordWithIndefiniteArticles,
} from "@/utils";
import { isSolarBatteryJourney } from "@/features/SolarForm/utils/formJourney";

import { sharedContent as content } from "./content.json";
import { DialogTrigger, UnderlinedText } from "@/components";
import { useFormContext } from "react-hook-form";

type BillVisionProps = {
  recommendationSystem:
    | IISystemSizeMinMaxResponse
    | IISolarBatteryOutlookResponse;
};

const ComponentWithWhiteColor = styled.div(() => ({
  "&& > *": {
    color: "white",
  },
}));

export const BILL_PERIODS = {
  [BillFrequencyEnum["MONTHLY"]]: "month",
  [BillFrequencyEnum["QUARTERLY"]]: "quarter",
};

export const BillVision = memo(({ recommendationSystem }: BillVisionProps) => {
  const { testId } = content;
  const {
    size,
    billSavingPeriod = BillFrequencyEnum["MONTHLY"],
    billAmount,
    billSavingPercent = 0,
    billSavingsDollarAbsolute,
    totalBillAfterSavingForBillFrequency,
  } = recommendationSystem;
  const { getValues } = useFormContext();
  const systemType = getValues("systemType");

  const billPeriod =
    billSavingPeriod && BILL_PERIODS[billSavingPeriod]
      ? BILL_PERIODS[billSavingPeriod].toLowerCase()
      : "month";

  return (
    <Box
      data-id={testId.billVision}
      display="flex"
      marginX={{ xs: "none", lg: "medium" }}
      marginY={{ xs: "small", lg: "none" }}
      width="full"
    >
      <Card outline>
        <Box
          display="flex"
          flexDirection="column"
          height="full"
          justifyContent="space-between"
        >
          <Stack space="medium">
            {!isSolarBatteryJourney(systemType) && (
              <Heading variant="h4" weight="bold">
                {content.myBillsLookLike.heading}
              </Heading>
            )}
            <Text variant="body-small">
              {formatLabel(
                content.myBillsLookLike.description,
                (value: string | number) => {
                  if (typeof value === "string" && value.includes("%")) {
                    return value;
                  }
                  const systemSize = formatWordWithIndefiniteArticles(
                    value.toString()
                  );
                  return (
                    <b>
                      {systemSize[0].toUpperCase() + systemSize.substring(1)}
                    </b>
                  );
                },
                `${size}kW`,
                `${billSavingPercent}%`
              )}
            </Text>
          </Stack>

          <Box
            display="flex"
            height="full"
            marginTop={{ xs: "none", lg: "xsmall" }}
            marginRight={{
              xs: "none",
              sm: "large",
              md: "xxxlarge",
              lg: "none",
            }}
          >
            <Box
              display="flex"
              alignItems="flex-end"
              marginTop={{ xs: "medium" }}
              marginRight={{
                xs: "none",
                sm: "xxxlarge",
                md: "xxxlarge",
                lg: "none",
              }}
              style={{ minHeight: 200 }}
            >
              <Box
                height="full"
                padding="medium"
                backgroundColor="grey100"
                borderRadius="medium"
                marginRight="xxsmall"
              >
                <Stack space="xxsmall">
                  <Heading variant="h3" tone="neutralDark">
                    {`$${formatNumberWithCommas(billAmount)}`}
                  </Heading>
                  <Text variant="body-small" tone="neutralDark">
                    {formatLabel(
                      content.myBillsLookLike.currentAverageBill,
                      (value) => value,
                      `${billSavingPeriod!.toLowerCase()}`
                    )}
                  </Text>
                </Stack>
              </Box>
              <Box
                padding="medium"
                backgroundColor="positive"
                borderRadius="medium"
                marginLeft="xxsmall"
                style={{
                  minHeight: `${100 - billSavingPercent}%`,
                  height: "fit-content",
                }}
              >
                <Stack space="xxsmall">
                  <ComponentWithWhiteColor>
                    <Heading variant="h3">{`$${totalBillAfterSavingForBillFrequency}`}</Heading>
                    <Text variant="body-small" tone="neutralDark">
                      {formatLabel(
                        content.myBillsLookLike.savingAverageBill,
                        (value) => value,
                        `$${billSavingsDollarAbsolute}`,
                        `${billPeriod}`
                      )}
                    </Text>
                  </ComponentWithWhiteColor>
                </Stack>
              </Box>
            </Box>
          </Box>
        </Box>
        <Text variant="body-small">
          <DialogTrigger
            id=""
            title={<>{content.assumptionText.heading}</>}
            summary={
              <List>
                {content.assumptions.map((assumptionPoint, index) => (
                  <ListItem key={index}>{assumptionPoint}</ListItem>
                ))}
              </List>
            }
          >
            <UnderlinedText>{content.assumptionText.heading}</UnderlinedText>
          </DialogTrigger>
        </Text>
      </Card>
    </Box>
  );
});
